/**
 * Automatizaciones — /app/renata/automatizaciones
 * Business automations. Generan propuestas; no hay ejecutor continuo en background.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Workflow, RefreshCw, Play, Pause, FlaskConical, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { InlineExtensionHint } from "@/components/renata/InlineExtensionHint";
import { BusinessEmptyState } from "@/components/renata/BusinessEmptyState";

const AUTO_STATUS_ES: Record<string, string> = {
  draft: "Borrador",
  active: "Lista (genera propuestas)",
  paused: "Pausada",
  error: "Con error",
  pending_approval: "Pendiente de aprobación",
  needs_review: "Requiere revisión",
};

export const Route = createFileRoute("/app/renata/automatizaciones")({
  component: AutomationsPage,
  head: () => ({ meta: [{ title: "Automatizaciones — Renata" }] }),
});

type Auto = {
  id: string;
  name: string;
  description: string;
  status: string;
  category: string;
  objective: string;
};

function AutomationsPage() {
  const tenantId = useWorkspaceTenantId();
  const [automations, setAutomations] = useState<Auto[]>([]);
  const [health, setHealth] = useState<Record<string, number>>({});
  const [catalog, setCatalog] = useState<Array<Record<string, unknown>>>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [intent, setIntent] = useState("Quiero automatizar el stock crítico");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/automation?view=overview", {
        headers: clientRequestHeaders(tenantId),
      });
      const data = await res.json();
      setAutomations(data.automations ?? []);
      setHealth(data.health ?? {});
      setCatalog(data.catalog ?? []);
    } finally {
      setBusy(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function post(action: string, body: Record<string, unknown> = {}) {
    if (!tenantId) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await fetch("/api/renata/automation", {
        method: "POST",
        headers: {
          ...clientRequestHeaders(tenantId),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action, ...body }),
      });
      const data = await res.json();
      setMsg(data.message ?? data.interpretation ?? data.error ?? "ok");
      await load();
      return data;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 pb-20 md:p-6">
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
        Una automatización <strong>lista</strong> puede generar propuestas cuando la ejecutás o cuando llega un disparador cableado.
        No corre sola en segundo plano de forma continua. Pausar sí bloquea nuevas corridas del pipeline.
      </div>

      <RenataPageChrome
        screenId="automations"
        title="Automatizaciones"
        subtitle="Automatizaciones de negocio. Nunca corren solas: siempre piden tu OK."
      />
      <InlineExtensionHint module="automatizaciones" />

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["active", "Activas"],
          ["paused", "Pausadas"],
          ["error", "Con error"],
          ["pending", "Pendientes"],
          ["needs_review", "Requieren revisión"],
          ["never_run", "Sin ejecutar"],
        ].map(([k, label]) => (
          <div key={k} className="rounded-lg border p-3">
            <div className="text-xs text-foreground/80">{label}</div>
            <div className="text-2xl font-semibold">{health[k] ?? 0}</div>
          </div>
        ))}
      </section>

      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Workflow className="h-4 w-4" /> Pedirle a Renata
        </h2>
        <textarea
          className="w-full rounded border bg-background p-2 text-sm"
          rows={2}
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
        />
        <Button
          size="sm"
          disabled={busy || !intent.trim()}
          onClick={() => void post("interpret", { text: intent })}
        >
          Interpretar y crear borrador
        </Button>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Tus automatizaciones</h2>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={busy}>
            <RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
        {automations.length === 0 ? (
          <BusinessEmptyState screenId="automations" />
        ) : (
          automations.map((a) => (
            <div key={a.id} className="space-y-2 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{AUTO_STATUS_ES[a.status] ?? a.status}</Badge>
                <Badge variant="outline">{a.category}</Badge>
                <span className="text-sm font-medium">{a.name}</span>
              </div>
              <p className="text-sm text-muted-foreground">{a.description}</p>
              <p className="text-xs">Objetivo: {a.objective}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("simulate", { automationId: a.id })}>
                  <FlaskConical className="mr-1 h-3.5 w-3.5" /> Simular
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => void post("run", { automationId: a.id })}>
                  <Play className="mr-1 h-3.5 w-3.5" /> Proponer corrida
                </Button>
                <Button size="sm" disabled={busy} onClick={() => void post("approve_activate", { automationId: a.id })}>
                  <Check className="mr-1 h-3.5 w-3.5" /> Aprobar y activar
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void post("pause", { automationId: a.id })}>
                  <Pause className="mr-1 h-3.5 w-3.5" /> Pausar
                </Button>
              </div>
            </div>
          ))
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Catálogo / plantillas</h2>
        <p className="text-xs text-foreground/80">
          Incluye plantillas Renata + Growth Automations + Cron Registry (SSOT). Nunca se activan solas.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {catalog.map((c) => (
            <div key={String(c.id)} className="rounded border p-3 text-sm">
              <div className="font-medium">{String(c.name)}</div>
              <p className="text-xs text-foreground/80">{String(c.description)}</p>
              <div className="mt-2 flex gap-2">
                <Badge variant="outline">{String(c.source)}</Badge>
                {String(c.source) === "renata_template" ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7 text-[11px]"
                    disabled={busy}
                    onClick={() => void post("from_template", { templateId: c.id })}
                  >
                    Usar plantilla
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      {msg ? <p className="text-sm text-muted-foreground">{msg}</p> : null}
      <p className="text-xs text-foreground/80">
        Tu siguiente paso es simular una automatización o pedirle a Renata un borrador.
      </p>
    </div>
  );
}
